import _ from "lodash";
import type { GerberFileLayerMapping } from "../../../../reducers/uploadedBoard";

export interface DuplicateLayerFile {
  file: string;
  layers: string[];
}

function layerTitle(layerName: string): string {
  return _.capitalize(layerName.replaceAll("_", " "));
}

/**
 * Files that have been chosen for more than one layer. A multi-select layer
 * counts once per file, so a file listed twice as an inner copper layer is a
 * duplicate too.
 */
export function findDuplicateLayerFiles(
  mapping: GerberFileLayerMapping | null,
): DuplicateLayerFile[] {
  if (mapping === null) {
    return [];
  }
  const layersByFile = new Map<string, string[]>();
  Object.entries(mapping).forEach(([layerName, selection]) => {
    const files = Array.isArray(selection) ? selection : [selection];
    files.forEach((file) => {
      if (!file) {
        return;
      }
      const layers = layersByFile.get(file) ?? [];
      layers.push(layerName);
      layersByFile.set(file, layers);
    });
  });
  return [...layersByFile.entries()]
    .filter(([, layers]) => {
      return layers.length > 1;
    })
    .map(([file, layers]) => {
      return { file, layers };
    });
}

/** One sentence per duplicated file. */
export function describeDuplicateLayerFiles(
  duplicates: DuplicateLayerFile[],
): string[] {
  return duplicates.map(({ file, layers }) => {
    const titles = _.uniq(layers.map(layerTitle));
    const where =
      titles.length === 1
        ? `${titles[0]} more than once`
        : `both ${titles.slice(0, -1).join(", ")} and ${titles[titles.length - 1]}`;
    return `${file} is selected for ${where}.`;
  });
}
